export interface SessionTrackerOptions {
	id: symbol;

	/** Set true to include its context in all events while the session is active. */
	influenced: boolean;
}

export interface Session<T = any, S = any> {
	/** Arbitrary data to attach to the session. */
	data: T;

	/**
	 * Page name, url, or other starting point of the session.
	 * Useful for lifecycle management.
	 */
	start: S;

	/** If true, the session's contexts will be included in all events. */
	influenced: boolean;

	createdAt: number;
}

const globalSessions = {} as Record<symbol, Session>;

/** Get the Snowplow contexts in active influenced sessions if any. */
export function getInfluencedSessionsContexts() {
	return Object.getOwnPropertySymbols(globalSessions)
		.map(key => globalSessions[key])
		.filter(session => session != null && session.influenced)
		.map(session => session!.data)
		.flat();
}

export function createSessionManager<T, S = any>({ id, influenced }: SessionTrackerOptions) {
	function attachSession(start: S, data: T) {
		globalSessions[id] = { data, start, createdAt: Date.now(), influenced };
	}

	function killSession(): Session<T> | null {
		const prevSession = session();
		delete globalSessions[id];

		return prevSession;
	}

	function session(): Session<T> | null {
		return globalSessions[id] ?? null;
	}

	return {
		session,
		attachSession,
		killSession,
	};
}
